import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { FileText, Mail, HelpCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import TutorialModal from "./TutorialModal";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [termsOpen, setTermsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/app-config`)
      .then((res) => setLogoUrl(res.data.logo_url || null))
      .catch(() => {});
  }, []);

  const handleSendContact = async () => {
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setSending(true);
    try {
      // Open mailto as fallback - simpler and always works
      window.location.href = `mailto:info@ibiid.com?subject=Contacto desde Clueer - ${contactForm.name}&body=${encodeURIComponent(`De: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMensaje:\n${contactForm.message}`)}`;
      toast.success("Abriendo cliente de correo...");
      setContactForm({ name: "", email: "", message: "" });
      setContactOpen(false);
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <footer className="bg-[#F1F5F9] text-[#0F172A] py-6 mt-8 border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo & Branding - Single logo */}
            <div className="flex items-center gap-1">
              <img 
                src={logoUrl || "/logo-marketvalley.png"} 
                alt="Clueer" 
                className="h-6 sm:h-8 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="text-[8px] relative -top-2 -ml-0.5" title="Panamá">🇵🇦</span>
            </div>
            
            {/* Links */}
            <div className="flex items-center gap-6 text-sm">
              <button 
                onClick={() => setTermsOpen(true)}
                className="flex items-center gap-1.5 text-[#64748B] hover:text-[#0F172A] transition-colors"
              >
                <FileText size={14} />
                Términos y Privacidad
              </button>
              <button 
                onClick={() => setContactOpen(true)}
                className="flex items-center gap-1.5 text-[#64748B] hover:text-[#0F172A] transition-colors"
              >
                <Mail size={14} />
                Contacto
              </button>
              <button 
                onClick={() => setShowTutorial(true)}
                className="flex items-center gap-1.5 text-[#64748B] hover:text-[#0F172A] transition-colors"
              >
                <HelpCircle size={14} />
                FAQ
              </button>
            </div>
            
            {/* Copyright */}
            <div className="text-sm text-[#64748B]">
              © {currentYear} Clueer
            </div>
          </div>
          
          {/* Tagline */}
          <div className="text-center mt-4 pt-4 border-t border-[#E2E8F0]">
            <p className="text-xs text-[#94A3B8]">
              Marketplace de tecnología con subastas y venta directa en Panamá
            </p>
          </div>
        </div>
      </footer>

      {/* Tutorial/FAQ Modal */}
      <TutorialModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      {/* Terms & Privacy Dialog */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#475569]" />
              Términos y Privacidad
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 text-sm text-[#374151]">
            <section>
              <h3 className="font-semibold text-[#0F172A] mb-2">Uso del Sitio</h3>
              <p>
                Clueer es una plataforma de marketplace para compra y venta de productos tecnológicos 
                mediante subastas y venta directa. Al usar nuestro sitio, aceptas las siguientes condiciones:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-[#64748B]">
                <li>Debes tener al menos 18 años para realizar transacciones</li>
                <li>Los precios de cierre en subastas no son negociables</li>
                <li>Todas las transacciones son contraentrega</li>
                <li>Los pagos se realizan únicamente por Yappy o transferencia bancaria</li>
                <li>Tienes derecho a revisar el producto antes de pagar</li>
              </ul>
            </section>
            
            <section>
              <h3 className="font-semibold text-[#0F172A] mb-2">Privacidad</h3>
              <p>
                Respetamos tu privacidad. La información que recopilamos se usa únicamente para:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-[#64748B]">
                <li>Gestionar tu cuenta y transacciones</li>
                <li>Enviarte notificaciones sobre tus compras y subastas</li>
                <li>Coordinar entregas de productos</li>
                <li>Mejorar nuestros servicios</li>
              </ul>
              <p className="mt-2">
                <strong>No compartimos</strong> tu información personal con terceros sin tu consentimiento.
              </p>
            </section>
            
            <section>
              <h3 className="font-semibold text-[#0F172A] mb-2">Contacto</h3>
              <p>
                Para cualquier consulta sobre estos términos, contáctanos en{" "}
                <a href="mailto:info@ibiid.com" className="text-[#F97316] hover:underline">
                  info@ibiid.com
                </a>
              </p>
            </section>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setTermsOpen(false)} className="rounded-full bg-[#475569] hover:bg-[#334155]">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#475569]" />
              Contáctanos
            </DialogTitle>
            <DialogDescription>
              Envíanos un mensaje y te responderemos pronto
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Input
                placeholder="Tu nombre"
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="Tu email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Textarea
                placeholder="¿En qué podemos ayudarte?"
                value={contactForm.message}
                onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                className="rounded-xl min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setContactOpen(false)} className="rounded-full">
              Cancelar
            </Button>
            <Button 
              onClick={handleSendContact}
              disabled={sending || !contactForm.name || !contactForm.email || !contactForm.message}
              className="rounded-full bg-[#F97316] hover:bg-[#EA580C]"
            >
              {sending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Footer;
